/**
 * Telemetry tab rows (ENG-232), per source family.
 *
 * Which row a source can show comes from ENG-231's comparison matrix
 * (DRONE_TELEMETRY_DATA_SOURCES.md): a row is listed for a family only when that
 * family really publishes the value. A field a family does not send is left out,
 * not shown as "—"; a field it does send but that is missing right now shows "—".
 * Every value is optional on the wire.
 */
import { droneContextFlags } from '@/lib/droneContextFlags';
import {
  formatAccuracyM,
  formatDbm,
  formatHeading,
  formatPercent,
  formatSpeedMps,
  formatVerticalSpeedMps,
  metresToFeet,
} from '@/lib/format';
import type { TelemetryEnvelope } from '@/lib/mqtt/schemas/telemetry';
import type { TelemetryFamily } from '@/lib/store/drones';

const NONE = '—';

export interface TelemetryRow {
  key: string;
  label: string;
  value: string;
  /** Second line under the value (units, caveat). */
  sub?: string;
  /** Colours the value: a connection that is down reads red. */
  tone?: 'ok' | 'crit';
}

export interface TelemetryGroup {
  id: 'position' | 'motion' | 'altitude' | 'state' | 'link' | 'source' | 'attitude' | 'air';
  title: string;
  rows: TelemetryRow[];
}

type Family = TelemetryFamily;
const ALL: readonly Family[] = ['mavlink', 'dji', 'dronesense', 'liveads', 'other'];
const MAV: readonly Family[] = ['mavlink'];
// 'other' (partner / Network ID streams, unclassified sources) is a best guess: it may carry
// battery or arm state, so those rows are offered but only shown when they have a value.
const MAV_DJI: readonly Family[] = ['mavlink', 'dji', 'other'];

interface RowDef {
  key: string;
  label: string;
  families: readonly Family[];
  build: (t: TelemetryEnvelope, family: Family) => Pick<TelemetryRow, 'value' | 'sub' | 'tone'>;
  /** Offered only once it has a value: these come from the raw GCS stream, which
   *  is subscribed for the selected aircraft only, so no permanent blanks. */
  hideWhenEmpty?: boolean;
}

const yesNo = (v: boolean | null | undefined) => (v == null ? NONE : v ? 'Yes' : 'No');
const feet = (m: number | null | undefined) => {
  const ft = metresToFeet(m);
  return ft == null ? NONE : `${Math.round(ft).toLocaleString()} ft`;
};
const metres = (m: number | null | undefined) => (m == null ? undefined : `${Math.round(m)} m`);
const withUnit = (v: number | null | undefined, unit: string, dp = 0) =>
  v == null ? NONE : `${v.toFixed(dp)}${unit}`;

const SOURCE_LABEL: Record<Family, string> = {
  mavlink: 'MAVLink',
  dji: 'DJI / FH2',
  dronesense: 'DroneSense',
  liveads: 'LiveADS',
  other: 'Unknown',
};

const GROUPS: { id: TelemetryGroup['id']; title: string; rows: RowDef[] }[] = [
  {
    id: 'position',
    title: 'Position',
    rows: [
      {
        key: 'lat',
        label: 'Latitude',
        families: ALL,
        build: (t) => ({ value: Number.isFinite(t.lat) ? t.lat.toFixed(5) : NONE }),
      },
      {
        key: 'lon',
        label: 'Longitude',
        families: ALL,
        build: (t) => ({ value: Number.isFinite(t.lng) ? t.lng.toFixed(5) : NONE }),
      },
    ],
  },
  {
    id: 'motion',
    title: 'Motion',
    rows: [
      {
        key: 'ground_speed',
        label: 'Ground Speed',
        families: ALL,
        build: (t) => ({ value: formatSpeedMps(t.groundspeed) }),
      },
      {
        // DroneSense publishes a constant 0, which would read as a real reading.
        key: 'vertical_speed',
        label: 'Vertical Speed',
        families: ['mavlink', 'dji', 'liveads', 'other'],
        build: (t) => ({ value: formatVerticalSpeedMps(t.climbRate) }),
      },
      {
        key: 'heading',
        label: 'Heading',
        families: ALL,
        build: (t) => ({ value: t.heading_known === false ? NONE : formatHeading(t.heading) }),
      },
      {
        key: 'airspeed',
        label: 'Airspeed',
        families: MAV,
        hideWhenEmpty: true,
        build: (t) => ({ value: formatSpeedMps(t.airspeed_mps) }),
      },
      {
        key: 'throttle',
        label: 'Throttle',
        families: MAV,
        hideWhenEmpty: true,
        build: (t) => ({ value: withUnit(t.throttle_pct, '%') }),
      },
    ],
  },
  {
    id: 'altitude',
    title: 'Altitude',
    rows: [
      {
        key: 'alt_msl',
        label: 'Altitude MSL',
        families: ALL,
        build: (t) => ({ value: feet(t.asl), sub: metres(t.asl) }),
      },
      {
        // LiveADS publishes no AGL.
        key: 'alt_agl',
        label: 'Altitude AGL',
        families: ['mavlink', 'dji', 'dronesense', 'other'],
        build: (t, f) => ({
          value: t.agl == null ? NONE : `${Math.round(t.agl).toLocaleString()} ft`,
          sub:
            t.aglM != null
              ? `${Math.round(t.aglM)} m${f === 'dji' ? ' · above take-off' : f === 'mavlink' ? ' · above home' : ''}`
              : undefined,
        }),
      },
      {
        // DJI's is synthesised from `height`; DroneSense sends 0 — not a reading.
        key: 'alt_pressure',
        label: 'Pressure Altitude',
        families: ['mavlink', 'dji', 'liveads', 'other'],
        build: (t, f) => ({
          value: feet(t.pressure_altitude),
          sub:
            t.pressure_altitude == null
              ? undefined
              : `${Math.round(t.pressure_altitude)} m${f === 'dji' ? ' · estimated' : ''}`,
        }),
      },
    ],
  },
  {
    id: 'state',
    title: 'State',
    rows: [
      {
        key: 'flight_mode',
        label: 'Flight Mode',
        families: ALL,
        build: (t) => ({ value: t.flight_mode ?? NONE }),
      },
      {
        // ADS-B and DroneSense carry no arm state.
        key: 'armed',
        label: 'Armed',
        families: MAV_DJI,
        build: (t) => ({ value: yesNo(t.armed) }),
      },
      {
        key: 'landed_state',
        label: 'Landed State',
        families: MAV,
        // ENG-271: shown only when the aircraft really reports one (MAV_LANDED_STATE).
        // The raw stream's UTM flight_state is not a reliable stand-in.
        hideWhenEmpty: true,
        build: (t) => ({ value: t.landed_state ?? NONE }),
      },
      {
        // ADS-B and DroneSense carry no battery.
        key: 'battery',
        label: 'Battery',
        families: MAV_DJI,
        build: (t) => ({
          value: formatPercent(t.battery_pct),
          sub: t.battery_v != null ? `${t.battery_v.toFixed(1)} V` : undefined,
        }),
      },
      {
        key: 'connected',
        label: 'Connected',
        families: ALL,
        build: (t) => ({
          value: yesNo(t.adapter_connected),
          tone: t.adapter_connected == null ? undefined : t.adapter_connected ? 'ok' : 'crit',
        }),
      },
    ],
  },
  {
    id: 'link',
    title: 'Link & Accuracy',
    rows: [
      {
        key: 'link_rssi',
        label: 'Link RSSI',
        families: MAV,
        build: (t) => ({ value: formatDbm(t.link_rssi_dbm) }),
      },
      // DJI and LiveADS publish constants, DroneSense 0: only MAVLink's are measurements.
      {
        key: 'acc_h',
        label: 'GPS Accuracy (H)',
        families: MAV,
        build: (t) => ({ value: formatAccuracyM(t.remote_id?.horiz_accuracy_m ?? null) }),
      },
      {
        key: 'acc_v',
        label: 'GPS Accuracy (V)',
        families: MAV,
        build: (t) => ({
          value: formatAccuracyM(t.vert_accuracy_m ?? t.remote_id?.vert_accuracy_m ?? null),
        }),
      },
      {
        key: 'acc_speed',
        label: 'Speed Accuracy',
        families: MAV,
        build: (t) => ({
          value: t.speed_accuracy_mps == null ? NONE : `±${t.speed_accuracy_mps.toFixed(1)} m/s`,
        }),
      },
    ],
  },
  {
    id: 'attitude',
    title: 'Attitude',
    rows: [
      {
        key: 'roll',
        label: 'Roll',
        families: MAV,
        hideWhenEmpty: true,
        build: (t) => ({ value: withUnit(t.roll_deg, '°', 1) }),
      },
      {
        key: 'pitch',
        label: 'Pitch',
        families: MAV,
        hideWhenEmpty: true,
        build: (t) => ({ value: withUnit(t.pitch_deg, '°', 1) }),
      },
      {
        key: 'yaw',
        label: 'Yaw',
        families: MAV,
        hideWhenEmpty: true,
        build: (t) => ({ value: withUnit(t.yaw_deg, '°') }),
      },
    ],
  },
  {
    id: 'air',
    title: 'Air',
    rows: [
      {
        key: 'air_pressure',
        label: 'Air Pressure',
        families: MAV,
        hideWhenEmpty: true,
        build: (t) => ({ value: withUnit(t.air_pressure_hpa, ' hPa', 1) }),
      },
      {
        key: 'air_temp',
        label: 'Air Temperature',
        families: MAV,
        hideWhenEmpty: true,
        build: (t) => ({ value: withUnit(t.air_temp_c, ' °C', 1) }),
      },
    ],
  },
  {
    id: 'source',
    title: 'Data Source',
    rows: [
      {
        key: 'source',
        label: 'Source',
        families: ALL,
        // An aircraft whose source we can't classify (partner stream, Network ID) is
        // labelled by its own stream name rather than "Unknown". The publisher's raw `source`
        // string (e.g. "aircraft_agent") is deliberately not shown: next to the Overview's
        // "Aircraft Agent" tile it reads as an agent status, which it is not.
        build: (t, f) => ({
          value: f === 'other' ? (t.source_label ?? SOURCE_LABEL.other) : SOURCE_LABEL[f],
        }),
      },
    ],
  },
];

/** For an unclassified source, only these are always shown; every other row appears only
 *  once it has a value, so such an aircraft is not full of permanent placeholders. */
const ALWAYS_FOR_OTHER = new Set(['lat', 'lon', 'ground_speed', 'heading', 'alt_msl', 'source']);

export function buildTelemetryGroups(
  t: TelemetryEnvelope,
  family: TelemetryFamily,
): TelemetryGroup[] {
  // ENG-271: Battery and Link RSSI are hidden while nothing real feeds them.
  const hidden = new Set<string>();
  if (!droneContextFlags.telemetry.battery) hidden.add('battery');
  if (!droneContextFlags.telemetry.linkRssi) hidden.add('link_rssi');
  return GROUPS.map((g) => ({
    id: g.id,
    title: g.title,
    rows: g.rows
      .filter((r) => r.families.includes(family) && !hidden.has(r.key))
      .map((r) => ({ def: r, built: { key: r.key, label: r.label, ...r.build(t, family) } }))
      .filter(({ def, built }) => !def.hideWhenEmpty || built.value !== NONE)
      .map(({ built }) => built)
      .filter((r) => family !== 'other' || ALWAYS_FOR_OTHER.has(r.key) || r.value !== NONE),
  })).filter((g) => g.rows.length > 0);
}

/** How many of a group's rows have a value — shown in the accordion header. */
export function filledCount(g: TelemetryGroup): string {
  const filled = g.rows.filter((r) => r.value !== NONE).length;
  return `${filled}/${g.rows.length}`;
}
